import {
  ListTable,
  ModalMessage,
  LoadingSpinner,
  PaginationMenu,
  SortDropdown,
} from "../../components";
import { Container, Row, Col } from "react-bootstrap";
import { adminDeleteUserApi, adminFetchUsersApi } from "../../api/usersApi.js";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getStorageToken, useLogout } from "../../utilities";

export default function AdminUsersList() {
  // displays a list of all users with the option for sorting/filtering
  // admin can delete a user

  const tableHeaders = ["Last Name", "First Name", "Email", "Is Admin"];
  const fieldNames = ["lastName", "firstName", "email", "isAdmin"];

  const initialPaginationState = {
    pageNr: 1,
    totalNrOfPages: 1,
  };

  const sortOptions = [
    { value: "lastName_1", label: "Last Name: A-Z" },
    { value: "lastName_-1", label: "Last Name: Z-A" },
    { value: "firstName_1", label: "First Name: A-Z" },
    { value: "firstName_-1", label: "First Name: Z-A" },
    { value: "email_1", label: "Email: A-Z" },
    { value: "email_-1", label: "Email: Z-A" },
    { value: "createdAt_-1", label: "Account: Newest" },
    { value: "createdAt_1", label: "Account: Oldest" },
  ];

  const filterOptions = [
    { value: "", label: "None" },
    { value: "isAdmin_1", label: "Admin" },
    { value: "isAdmin_0", label: "Regular User" },
  ];

  const { stayLoggedIn } = useSelector((state) => state.user);
  const logout = useLogout();

  const [users, setUsers] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paginationState, setPaginationState] = useState(
    {...initialPaginationState}
  );
  const [updateUsers, setUpdateUsers] = useState(true);

  const [sortOption, setSortOption] = useState(sortOptions[0]?.value);
  const [filterOption, setFilterOption] = useState(filterOptions[0]?.value);
  const [showAlertMsg, setShowAlertMsg] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchUsers = async (pageNr = null) => {
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await adminFetchUsersApi(
          token,
          abortController.signal,
          pageNr,
          sortOption,
          filterOption
        );
        return res;
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    if (updateUsers) {
      const pageNr = paginationState?.pageNr || 1;
      setUpdateUsers(false);
      fetchUsers(pageNr)
        .then((res) => {
          setUsers(res?.users);
          setPaginationState({
            pageNr: res?.currPageNr || 1,
            totalNrOfPages: res?.totalNrOfPages || 1,
          });
        })
        .catch((error) =>
          setErrorMsg("Could not load data. Please try again later.")
        );
    }

    return () => {
      abortController.abort();
    };
  }, [
    updateUsers,
    filterOption,
    sortOption,
    paginationState?.pageNr,
    stayLoggedIn,
    logout,
  ]);

  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    setPaginationState({ ...paginationState, pageNr: newPageNr });
    setUpdateUsers(true);
  };
  
  const handleConfirm = async () => {
    // calls api function to delete a user with userId
      try {
        setIsLoading(true);
        const token = getStorageToken(stayLoggedIn);
        const abortController = new AbortController();
        await adminDeleteUserApi(token, showAlertMsg.payload, abortController.signal);
        setUpdateUsers(true);
        setSuccessMsg("The user has been deleted."); //after isLoading finished
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("The user could not be deleted. Please try again later. ");
      } finally {
        setIsLoading(false);
      }
  };

  const handleChangeSortKey = (newKey) => {
    setSortOption(newKey);
    setUpdateUsers(true);
  };

  const handleChangeFilterKey = (newKey) => {
    setFilterOption(newKey);
    setUpdateUsers(true);
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <h1>Users</h1>
      {isLoading && <LoadingSpinner />}
      {users && (
        <Row xs={2} md={3} lg={4} className="mt-4 mb-1">
          <Col>
            <SortDropdown
              sortOptions={sortOptions}
              handleChangeSortKey={handleChangeSortKey}
            />
          </Col>
          <Col>
            <SortDropdown
              sortOptions={filterOptions}
              heading="Filter By"
              handleChangeSortKey={handleChangeFilterKey}
            />
          </Col>
        </Row>
      )}
      {users?.length > 0 && (
        <ListTable
          dataArray={users}
          headers={tableHeaders}
          fieldNames={fieldNames}
          view={`/admin/user-details`}
          delete={(userId) => setShowAlertMsg({show: true, payload: userId})}
        />
      )}

      {/* PAGINATION MENU */}
      {users && users.length > 0 && (
        <PaginationMenu
          pageNr={paginationState.pageNr}
          totalNrOfPages={paginationState.totalNrOfPages}
          handleChangePageNr={handleChangePageNr}
        />
      )}
      {/* alert message */}
      {showAlertMsg?.show && (
        <ModalMessage
          message="Are you sure you want to delete this user?"
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirm}
          setShowAlertMsg={setShowAlertMsg}
        />
      )}
    </Container>
  );
}
